import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TablePagination } from '@mui/material';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useCallback, useEffect, useState } from 'react';

import { CURRENCY_FORMATTER, PAGE_INITIAL_VALUE, PRODUCT_ROWS_PER_PAGE_OPTION } from 'common/constants';
import { theme } from 'v3/styles/global-styles';

import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setLimit, setPage, setProductsSkus, setSkuSelected } from 'v3/data/store/product/actions';

import { changeProductLmi } from 'v3/pages/Products/List/useCases/change-product-lmi';
import * as api from 'v3/services/api';
import { getAllProductsSku } from 'v3/services/api';
import { ISku } from 'v3/services/interfaces/response';

import { SmartphoneFilter } from 'v3/shared/components/Filters/SmartphoneFilter';
import { ChangeValueModal } from 'v3/shared/components/Modal/ChangePriceModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { PaperStyled } from 'v3/shared/components/Table/styles';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';

import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { MenuProductList } from 'v3/shared/components/Inputs/MenuProductList';
import { ProductMobileTable } from 'v3/shared/components/MobileTable/ProductMobileTable';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import {
  Container,
  Content,
  DeviceImg,
  Header,
  MoreVertIconStyled,
  SelectPropsStyles,
  TableCellGroup,
  TableCellInColumn,
  TableCellInRow,
  TableCellStyled,
  TableContainerStyled,
  TablePaginationStyles,
  TableRowStyled,
} from '../styles';

export const MasterAdminProductsListPage = () => {
  const { limit, page, model } = useStoreSelector((store) => store.products.productsFilter);
  const { sku, skuSelected } = useStoreSelector((store) => store.products);
  const products = useStoreSelector((store) => store.products.sku);
  const [changeLmiModalOpen, setChangeLmiModalOpen] = useState(false);
  const { isDesktop } = useBreakpoints();
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_3074_IMPLEMENT_MAXIMUM_VALUES);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  const fetchProductList = useCallback(async () => {
    try {
      const response = await getAllProductsSku({ page, limit, model });
      setProductsSkus({ items: response.data.items, count: response.data.count });
    } catch (error) {
      showErrorDialog('Erro ao obter lista de produtos', error);
    }
  }, [page, limit, model]);

  const handleOpenEditProductLmiModal = (sku: ISku) => {
    setSkuSelected(sku);
    setChangeLmiModalOpen(true);
  };

  const changeLmiHandler = async (lmi: number) => {
    await changeProductLmi({ ...skuSelected, lmi: Number(lmi) / CURRENCY_FORMATTER });
  };

  useEffect(() => {
    fetchProductList();
  }, [page, limit, model]);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <HeadlineMedium>Lista de produtos</HeadlineMedium>
          </Header>
          <SmartphoneFilter />
          {isDesktop ? (
            <PaperStyled>
              <TableContainerStyled>
                {sku.items.isEmpty ? (
                  <NoItemsFound />
                ) : (
                  <TableMui stickyHeader aria-label="sticky tables">
                    <TableHead>
                      <TableRow>
                        <TableCellStyled>Modelo</TableCellStyled>
                        <TableCellStyled>LMI</TableCellStyled>
                        <TableCellStyled>Preço base</TableCellStyled>
                        {sellerFeatureEnabled && <TableCellStyled>Mais opções</TableCellStyled>}
                        {/* <TableCellStyled>Seguros</TableCellStyled> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sku?.items?.map((sku: ISku) => (
                        <TableRowStyled key={sku.id}>
                          <TableCellStyled sx={{ backgroundColor: 'transparent' }}>
                            <TableCellInRow>
                              <DeviceImg src={sku.image.marketingUrl} alt={sku.product.name} />
                              <TableCellInColumn>
                                <SubtitleMedium>{sku.product.name}</SubtitleMedium>
                                <SubtitleMedium color={theme.palette.secondary.main}>
                                  {` ${sku.storage}GB`}
                                </SubtitleMedium>
                              </TableCellInColumn>
                            </TableCellInRow>
                          </TableCellStyled>

                          <TableCellGroup>
                            <TableCellInRow>
                              <SubtitleMedium>{`R$ ${sku.lmi.toFixed(2)}`}</SubtitleMedium>
                              <EditOutlinedIcon onClick={() => handleOpenEditProductLmiModal(sku)} />
                            </TableCellInRow>
                          </TableCellGroup>

                          <TableCellGroup>
                            <TableCellInRow>
                              <TableCellInColumn>
                                <SubtitleMedium>{`R$ ${sku.basePrice.toFixed(2)}`}</SubtitleMedium>
                                <SubtitleMedium color={theme.palette.secondary.main}>
                                  {`Valor máximo do aparelho: R$ ${sku.maxPrice.toFixed(2)}`}
                                </SubtitleMedium>
                              </TableCellInColumn>
                            </TableCellInRow>
                          </TableCellGroup>

                          {sellerFeatureEnabled && (
                            <TableCellGroup>
                              <TableCellInRow>
                                <MoreVertIconStyled>
                                  <MenuProductList sku={sku} />
                                </MoreVertIconStyled>
                              </TableCellInRow>
                            </TableCellGroup>
                          )}
                          {/* <TableCellGroup>
                            <TableCellInRow>
                              <SubtitleMedium>2/4 habilitados</SubtitleMedium>
                              <EditOutlinedIcon onClick={() => handleOpenInsuranceModal(sku)} />
                            </TableCellInRow>
                          </TableCellGroup> */}
                        </TableRowStyled>
                      ))}
                    </TableBody>
                  </TableMui>
                )}
              </TableContainerStyled>
              <TablePagination
                rowsPerPageOptions={PRODUCT_ROWS_PER_PAGE_OPTION}
                component="div"
                sx={TablePaginationStyles}
                slotProps={{
                  select: {
                    ...SelectPropsStyles,
                    IconComponent: KeyboardArrowDownIcon,
                  },
                }}
                count={sku.count}
                rowsPerPage={limit}
                page={page}
                onPageChange={(event, value) => {
                  setPage(value);
                }}
                onRowsPerPageChange={(event) => {
                  setPage(PAGE_INITIAL_VALUE);
                  setLimit(Number(event.target.value));
                }}
              />
            </PaperStyled>
          ) : (
            <ProductMobileTable items={products.items}></ProductMobileTable>
          )}

          <ChangeValueModal
            modal={{
              initialPrice: skuSelected.lmi,
              active: changeLmiModalOpen,
              title: 'Editar preço do LMI',
              activeButton: true,
              cancelButton: 'Cancelar',
              saveButton: 'Salvar',
            }}
            onClose={() => setChangeLmiModalOpen(false)}
            onConfirm={changeLmiHandler}
          />
        </Content>
      </Container>
    </CommonTemplate>
  );
};
